/**
 * Ein Knopf, der waehrend seiner Arbeit zeigt, dass er arbeitet.
 *
 * 🔴 Regel Matze (14.09.2026): **Fortschritt gehoert in JEDEN Knopf**, nicht nur in die
 * mit Job-Verfolgung. Ein Knopf, der nach dem Klick zwei Minuten lang aussieht wie
 * vorher, ist der haeufigste Grund fuer den zweiten Klick — und der zweite Klick ist
 * hier teuer: „Artikel bilden" macht Leseabfragen gegen Plenty.
 *
 * Es gibt zwei Ehrlichkeitsstufen, und die Komponente vermischt sie nicht:
 *
 *   **echter Fortschritt** (`prozent` gesetzt) — ein gefuellter Balken. Nur dort, wo
 *   das Backend wirklich zaehlt, also beim Schreiblauf.
 *
 *   **kein Zaehler** (`prozent` fehlt) — ein pulsierender Balken plus die verstrichene
 *   Zeit. Kein erfundener Prozentwert: ein Balken, der bei 90 % stehenbleibt, ist
 *   schlimmer als gar keiner. Wo eine Erfahrungsdauer bekannt ist, steht sie als
 *   `dauerHinweis` daneben („dauert meist 1–2 Minuten") — das beantwortet die Frage,
 *   die der Prozentwert beantworten sollte.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@libero/core/components/ui/button';

export interface LaufButtonProps {
  /** Laeuft die Arbeit gerade? */
  laeuft: boolean;
  /** Beschriftung im Ruhezustand. */
  children: React.ReactNode;
  /** Beschriftung waehrend der Arbeit — Verb im Verlauf, z. B. „Datei wird gelesen". */
  laufText: string;
  onClick: () => void;
  disabled?: boolean;
  /** 0–100, wenn es einen echten Zaehler gibt. Sonst weglassen. */
  prozent?: number | null;
  /** Was gerade passiert, eine Zeile — z. B. „Artikel 73 von 241". */
  schritt?: string | null;
  /** Erfahrungswert, damit die Wartezeit einordenbar ist. */
  dauerHinweis?: string;
  variant?: 'default' | 'outline';
  size?: 'default' | 'sm';
  className?: string;
  /** Icon im Ruhezustand. */
  icon?: React.ReactNode;
}

export default function LaufButton({
  laeuft, children, laufText, onClick, disabled = false,
  prozent = null, schritt = null, dauerHinweis, variant = 'default',
  size = 'default', className, icon,
}: LaufButtonProps) {
  const sekunden = verstricheneSekunden(laeuft);
  const hatZaehler = prozent != null && Number.isFinite(prozent);

  return (
    <div className="space-y-1">
      <Button
        onClick={onClick}
        disabled={disabled || laeuft}
        variant={variant}
        size={size}
        className={className}
      >
        {laeuft
          ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden />
          : icon}
        {laeuft
          ? `${laufText} …${sekunden >= 3 ? ` (${dauer(sekunden)})` : ''}`
          : children}
      </Button>

      {laeuft && (
        <div className="max-w-md space-y-1" role="status" aria-live="polite">
          <div className="h-1.5 overflow-hidden rounded bg-[#DAD9D9]">
            <div
              className={`h-full rounded bg-[#E9FF57] ${hatZaehler ? 'transition-all' : 'animate-pulse'}`}
              style={{ width: hatZaehler ? `${Math.min(100, Math.max(0, prozent!))}%` : '100%' }}
            />
          </div>
          <p className="text-xs text-[#1A1A1A]/60">
            {hatZaehler && `${Math.round(prozent!)} % · `}
            {schritt ?? (hatZaehler ? null : 'läuft')}
            {dauerHinweis && !hatZaehler && ` · ${dauerHinweis}`}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Sekunden seit dem Start. Zaehlt nur, solange gearbeitet wird, und faengt bei jedem
 * neuen Lauf wieder bei null an.
 */
function verstricheneSekunden(laeuft: boolean): number {
  const [sekunden, setSekunden] = useState(0);

  useEffect(() => {
    if (!laeuft) {
      setSekunden(0);
      return;
    }
    const start = Date.now();
    const takt = window.setInterval(() => {
      setSekunden(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => window.clearInterval(takt);
  }, [laeuft]);

  return sekunden;
}

/** `95` → `1:35`. Unter einer Minute nur Sekunden. */
export function dauer(sekunden: number): string {
  if (sekunden < 60) return `${sekunden} s`;
  const minuten = Math.floor(sekunden / 60);
  return `${minuten}:${String(sekunden % 60).padStart(2, '0')} min`;
}
